import { BREAKPOINTS } from './constants.js';

// ... existing imports ...

// Responsive Helper (Lazy load to avoid cycle if possible, or direct import if safe)
// Since utils.js is low level, importing responsive.js might be cyclic if responsive.js uses utils.js.
// responsive.js does NOT use utils.js currently. It only imports constants.
// So safe to import.
import { resolveNode } from './utils/responsive.js';

/**
 * Calculate active breakpoint for a given width
 * Shared utility used by both Editor and Live renderer
 * @param {number} width - Width in pixels
 * @returns {string} Breakpoint key (default, sm, md, lg, xl, xxl)
 */
export const getBreakpointForWidth = (width) => {
    // Sort breakpoints descending by value: 2xl(1536), xl(1280), lg(1024), md(768), sm(640), xs(360), default(0)
    const sorted = Object.entries(BREAKPOINTS).sort((a, b) => b[1] - a[1]);

    // Find first breakpoint where width >= breakpoint value
    // This ensures: 0-359 => default, 360-639 => xs, 640-767 => sm, etc.
    for (const [bp, val] of sorted) {
        if (width >= val) {
            return bp;
        }
    }

    // Fallback (should never reach here if BREAKPOINTS contains default: 0)
    return 'default';
};

export const parseThemeColor = (theme, colorKey) => {
  if (!theme || !colorKey) return colorKey;
  
  // Handle both array and flattened object formats
  let value = '';
  if (Array.isArray(theme.colors)) {
    const colorObj = theme.colors.find(c => c.key === colorKey);
    value = colorObj ? colorObj.value : '';
  } else {
    const colors = theme.colors || theme;
    value = colors[colorKey] || '';
  }

  if (!value) return colorKey;
  return `rgb(${value.replace(/ /g, ', ')})`;
};

export const getThemeCssVariables = (theme) => {
  if (!theme) return {};
  const vars = {};
  
  if (Array.isArray(theme.colors)) {
    theme.colors.forEach(c => {
      vars[`--color-${c.key}`] = c.value;
    });
  } else {
    const colors = theme.colors || theme;
    const metadataKeys = ['type', 'name', 'description', 'isDefault', 'isActive', 'createdAt', 'updatedAt', '_id', 'version'];
    Object.entries(colors).forEach(([key, val]) => {
      if (typeof val === 'string' && !metadataKeys.includes(key)) {
          vars[`--color-${key}`] = val;
      }
    });
  }
  return vars;
};

// Converts Unity-style layout props to CSS
export const getAnchorStyle = (node, parentW, parentH, isLayoutManaged, contentSizeFitterConfig = false) => {
    if (isLayoutManaged) {
        const style = {
            position: 'relative',
            boxSizing: 'border-box'
        };

        // If anchors are stretched (min != max), use auto sizing to allow Flexbox stretch
        if (node.anchors && node.anchors.minX !== node.anchors.maxX) {
            style.width = 'auto';
        } else {
            style.width = node.width;
        }

        if (node.anchors && node.anchors.minY !== node.anchors.maxY) {
            style.height = 'auto';
        } else {
            style.height = node.height;
        }

        return style;
    }

    const { x, y, width, height, anchors } = node;

    // When ContentSizeFitter is applied, remove stretch anchors
    // and convert to fixed positioning based on which axis is enabled
    let effectiveAnchors = { ...anchors };

    if (contentSizeFitterConfig) {
        // If contentSizeFitterConfig is an object with horizontalFit/verticalFit props
        const horizontalFit = typeof contentSizeFitterConfig === 'object' ? contentSizeFitterConfig.horizontalFit : false;
        const verticalFit = typeof contentSizeFitterConfig === 'object' ? contentSizeFitterConfig.verticalFit : false;

        // Horizontal: if horizontalFit is On and stretched, convert to left anchor (0)
        if (horizontalFit === 'On' && effectiveAnchors.minX !== effectiveAnchors.maxX) {
            effectiveAnchors.minX = 0;
            effectiveAnchors.maxX = 0;
        }

        // Vertical: if verticalFit is On and stretched, convert to top anchor (0)
        if (verticalFit === 'On' && effectiveAnchors.minY !== effectiveAnchors.maxY) {
            effectiveAnchors.minY = 0;
            effectiveAnchors.maxY = 0;
        }

        // Fallback for boolean (backward compatibility)
        if (typeof contentSizeFitterConfig === 'boolean' && contentSizeFitterConfig === true) {
            // Horizontal: if stretched, convert to left anchor
            if (effectiveAnchors.minX !== effectiveAnchors.maxX) {
                effectiveAnchors.minX = 0;
                effectiveAnchors.maxX = 0;
            }

            // Vertical: if stretched, convert to top anchor
            if (effectiveAnchors.minY !== effectiveAnchors.maxY) {
                effectiveAnchors.minY = 0;
                effectiveAnchors.maxY = 0;
            }
        }
    }

    // CSS uses Top-Left origin.
    // Left = (ParentW * minX) + x
    // Width = (ParentW * (maxX - minX)) + width
    // Top = (ParentH * minY) + y
    // Height = (ParentH * (maxY - minY)) + height

    return {
        position: 'absolute',
        boxSizing: 'border-box',
        left: `calc(${effectiveAnchors.minX * 100}% + ${x}px)`,
        top: `calc(${effectiveAnchors.minY * 100}% + ${y}px)`,
        width: `calc(${(effectiveAnchors.maxX - effectiveAnchors.minX) * 100}% + ${width}px)`,
        height: `calc(${(effectiveAnchors.maxY - effectiveAnchors.minY) * 100}% + ${height}px)`
    };
};

// --- Hierarchy Helpers ---

// Resolves visual pixel rect relative to parent based on anchors
export const calculateRect = (node, parentW, parentH) => {
    const { x, y, width, height, anchors = { minX: 0, maxX: 0, minY: 0, maxY: 0 } } = node;
    const pixelLeft = (parentW * anchors.minX) + x;
    const pixelTop = (parentH * anchors.minY) + y;
    const pixelWidth = (parentW * (anchors.maxX - anchors.minX)) + width;
    const pixelHeight = (parentH * (anchors.maxY - anchors.minY)) + height;
    
    return { x: pixelLeft, y: pixelTop, width: pixelWidth, height: pixelHeight };
};

// Inverse: Converts visual pixel rect to layout props (x,y,w,h) based on current anchors
export const calculateLayout = (rect, anchors, parentW, parentH) => {
    // x = pixelLeft - (ParentW * minX)
    const x = rect.x - (parentW * anchors.minX);
    const y = rect.y - (parentH * anchors.minY);
    
    // width = pixelWidth - (ParentW * (maxX - minX))
    const width = rect.width - (parentW * (anchors.maxX - anchors.minX));
    const height = rect.height - (parentH * (anchors.maxY - anchors.minY));
    
    return { x, y, width, height };
};


export const getGlobalPosition = (nodeId, nodes, rootWidth, rootHeight) => {
  // We need to traverse up and resolve layout at each step.
  // This is expensive for deep trees, but accurate.
  const path = [];
  let curr = nodes.find(n => n.id === nodeId);
  while(curr) {
      path.unshift(curr);
      curr = nodes.find(n => n.id === curr?.parentId);
  }
  
  let currentX = 0;
  let currentY = 0;
  let parentW = rootWidth;
  let parentH = rootHeight;

  // This simple version assumes 100% scale and no complex transforms other than translation
  for (const node of path) {
      const rect = calculateRect(node, parentW, parentH);
      currentX += rect.x;
      currentY += rect.y;
      parentW = rect.width;
      parentH = rect.height;
  }
  
  return { x: currentX, y: currentY };
};

export const measureNode = (nodeId, nodes, rootW, rootH, activeBreakpoint = 'default') => {
    // 1. Try DOM measurement first (Unified Strategy)
    if (typeof document !== 'undefined' && nodeId) {
        const rootEl = document.getElementById('vbecs-canvas-root');
        const nodeEl = document.getElementById(nodeId);

        if (rootEl && nodeEl) {
            const scale = parseFloat(rootEl.getAttribute('data-scale') || '1');
            const nodeRect = nodeEl.getBoundingClientRect();
            const rootRect = rootEl.getBoundingClientRect();

            return {
                x: (nodeRect.left - rootRect.left - rootEl.clientLeft) / scale,
                y: (nodeRect.top - rootRect.top - rootEl.clientTop) / scale,
                width: nodeRect.width / scale,
                height: nodeRect.height / scale
            };
        }
    }

    if (!nodeId) return { x: 0, y: 0, width: rootW, height: rootH };
    
    // Build path to root
    const path = [];
    let curr = nodes.find(n => n.id === nodeId);
    while(curr) {
        path.unshift(curr);
        curr = nodes.find(n => n.id === curr.parentId);
    }
    
    // Traverse down to resolve actual dimensions
    let currentRect = { x: 0, y: 0, width: rootW, height: rootH }; 
    
    for (const node of path) {
        // Resolve node for responsive layout
        const resolvedNode = resolveNode(node, activeBreakpoint);

        // Calculate local rect based on PARENT dimensions
        const rect = calculateRect(resolvedNode, currentRect.width, currentRect.height);
        
        currentRect = {
            // Global Position = Parent Global + Local Relative
            x: currentRect.x + rect.x,
            y: currentRect.y + rect.y,
            width: rect.width,
            height: rect.height
        };
    }
    
    return currentRect;
};

export const getNodeChildren = (nodes, parentId) => {
  return nodes.filter(node => {
      if (parentId === undefined || parentId === null) {
          return node.parentId === undefined || node.parentId === null;
      }
      return node.parentId === parentId;
  });
};

export const findNodeUnderCursor = ({
  nodes, 
  x, 
  y, 
  rootW,
  rootH,
  excludeId,
  activeBreakpoint = 'default'
}) => {
    const candidates = [];
    
    // We need to build a map of computed rects to do this efficiently or accurately
    // For simplicity in this non-production env, we traverse.
    
    const resolve = (nodeId, pW, pH, currentGlobalX, currentGlobalY, depth) => {
        const children = nodes.filter(n => n.parentId === nodeId);
        // Sort by DOM order (last is on top)
        for (let i = children.length - 1; i >= 0; i--) {
            const child = children[i];
            if (child.id === excludeId) continue;
             // Check exclude descendant
            let p = child.parentId;
            let isDescendant = false;
            while(p) {
                if (p === excludeId) { isDescendant = true; break; }
                p = nodes.find(n => n.id === p)?.parentId;
            }
            if (isDescendant) continue;

            // Resolve node for responsive layout
            const resolvedChild = resolveNode(child, activeBreakpoint);

            // Check if disabled - skip disabled nodes
            const isEnabled = resolvedChild.props?.enabled !== false;
            if (!isEnabled) continue;

            // Check visibility (edit mode only)
            let isVisible = true;
            if (resolvedChild.props?.visibility !== undefined) {
                 if (typeof resolvedChild.props.visibility === 'boolean') {
                      isVisible = resolvedChild.props.visibility;
                 } else if (activeBreakpoint && typeof resolvedChild.props.visibility === 'object') {
                      if (resolvedChild.props.visibility[activeBreakpoint] === false) {
                           isVisible = false;
                      }
                 }
            }
            if (!isVisible) continue;

            const rect = calculateRect(resolvedChild, pW, pH);
            const gx = currentGlobalX + rect.x;
            const gy = currentGlobalY + rect.y;

            if (x >= gx && x <= gx + rect.width && y >= gy && y <= gy + rect.height) {
                if (!resolvedChild.props?.locked) {
                    candidates.push({ id: child.id, depth });
                }
            }
            
            // Recurse
            resolve(child.id, rect.width, rect.height, gx, gy, depth + 1);
        }
    };

    resolve(undefined, rootW, rootH, 0, 0, 0);

    candidates.sort((a, b) => b.depth - a.depth);
    return candidates.length > 0 ? candidates[0].id : undefined;
};

// --- Cloning Helpers ---

export const generateId = (prefix = 'Node') => {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
};

export const deepCloneNodes = (allNodes, rootId, injectSourceMapping = false) => {
    const rootNode = allNodes.find(n => n.id === rootId);
    if (!rootNode) return null;

    // 1. Find all descendants (BFS)
    const queue = [rootId];
    const nodesToCloneIds = new Set();

    while (queue.length > 0) {
        const currentId = queue.shift();
        nodesToCloneIds.add(currentId);
        const children = allNodes.filter(n => n.parentId === currentId);
        children.forEach(c => queue.push(c.id));
    }

    // 2. Create ID mapping for the subtree
    const idMap = new Map();
    nodesToCloneIds.forEach(oldId => {
        const node = allNodes.find(n => n.id === oldId);
        idMap.set(oldId, generateId(node.type));
    });

    // 3. Clone and remap properties
    const newNodes = [];
    nodesToCloneIds.forEach(oldId => {
        const original = allNodes.find(n => n.id === oldId);
        const newId = idMap.get(oldId);
        
        // Resolve new parent ID
        let newParentId = original.parentId;
        if (original.parentId && idMap.has(original.parentId)) {
            newParentId = idMap.get(original.parentId);
        }

        const newProps = original.props ? JSON.parse(JSON.stringify(original.props)) : {};
        if (injectSourceMapping) {
            newProps.sourceNodeId = original.id;
        }

        newNodes.push({
            ...original,
            id: newId,
            parentId: newParentId,
            // Deep clone props (safely)
            props: newProps,
            // Deep clone anchors
            anchors: { ...original.anchors },
            // Deep clone attachedComponents
            attachedComponents: original.attachedComponents
                ? JSON.parse(JSON.stringify(original.attachedComponents))
                : []
        });
    });

    return {
        newNodes,
        newRootId: idMap.get(rootId),
        idMap
    };
};
